import React from 'react';
import type { RotationPreview, RotationAction } from '../../../types/biweeklyRotation';
import { formatTimeUntil, formatPct, formatCurrency } from '../../../types/biweeklyRotation';

interface NextRunTabProps {
  preview: RotationPreview | null;
  loading: boolean;
  onRefresh: () => void;
  onSkipRotation: (reason: string) => void;
  onTriggerRotation: () => void;
}

const NextRunTab: React.FC<NextRunTabProps> = ({
  preview,
  loading,
  onRefresh,
  onSkipRotation,
  onTriggerRotation,
}) => {
  const [skipReason, setSkipReason] = React.useState('');
  const [showSkipModal, setShowSkipModal] = React.useState(false);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-neutral-400">Loading preview...</div>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="rounded-2xl border border-dashed border-white/10 bg-neutral-800/40 py-12 text-center">
        <p className="text-neutral-400">Failed to load rotation preview</p>
        <button onClick={onRefresh} className="mt-4 btn btn-outline px-4 py-2" type="button">
          Retry
        </button>
      </div>
    );
  }

  if (!preview.hasActiveUsers) {
    return (
      <div className="rounded-2xl border border-dashed border-white/10 bg-neutral-800/40 py-12 text-center">
        <svg
          className="w-12 h-12 mx-auto text-neutral-500 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
        <p className="text-neutral-400 text-lg font-medium">No Active Users</p>
        <p className="text-neutral-500 text-sm mt-1">
          {preview.message || 'No users have biweekly rotation enabled'}
        </p>
      </div>
    );
  }

  const handleSkip = () => {
    if (skipReason.trim()) {
      onSkipRotation(skipReason.trim());
      setShowSkipModal(false);
      setSkipReason('');
    }
  };

  const getActionColor = (type: RotationAction['type']) => {
    switch (type) {
      case 'EXIT':
      case 'TRIM':
        return 'text-red-400 bg-red-500/10 border-red-500/30';
      case 'ADD':
      case 'REFILL':
        return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30';
      case 'HEDGE':
      case 'UNHEDGE':
        return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
      default:
        return 'text-neutral-400 bg-neutral-500/10 border-neutral-500/30';
    }
  };

  return (
    <div className="space-y-6">
      {/* Schedule Header */}
      <div className="rounded-2xl border border-cyan-500/30 bg-gradient-to-r from-cyan-900/20 to-blue-900/20 p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Next Rotation Preview</h3>
            <p className="text-sm text-neutral-400 mt-1">
              Scheduled for {preview.formattedDate}
            </p>
            {preview.timeUntil && (
              <p className="text-sm text-cyan-400 mt-1">
                Executes in {formatTimeUntil(preview.timeUntil)}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onRefresh}
              className="btn btn-outline px-4 py-2"
              type="button"
            >
              Refresh
            </button>
            {preview.canExecute && (
              <button
                onClick={onTriggerRotation}
                className="btn px-4 py-2 bg-cyan-500 text-black hover:brightness-110"
                type="button"
              >
                Execute Now
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Execution Status */}
      {!preview.canExecute && preview.executionBlockedReason && (
        <div className="rounded-2xl border border-yellow-500/30 bg-yellow-500/10 p-4">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div>
              <p className="text-yellow-200 font-medium">Execution Blocked</p>
              <p className="text-yellow-100/80 text-sm mt-1">
                {preview.executionBlockedReason}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* What Will Happen */}
      <div className="rounded-2xl border border-white/10 bg-neutral-800/60 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">What Will Happen</h3>

        {preview.actions.length === 0 ? (
          <div className="rounded-xl bg-neutral-900/50 p-6 text-center">
            <p className="text-neutral-400">No actions planned for this rotation</p>
            <p className="text-neutral-500 text-sm mt-1">
              Portfolio is already optimized or no changes meet the criteria
            </p>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-3 text-center">
                <p className="text-xs text-neutral-400">Exits</p>
                <p className="text-xl font-bold text-red-300">{preview.summary.totalExits}</p>
              </div>
              <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-3 text-center">
                <p className="text-xs text-neutral-400">Adds</p>
                <p className="text-xl font-bold text-emerald-300">{preview.summary.totalAdds}</p>
              </div>
              <div className="rounded-xl bg-yellow-500/10 border border-yellow-500/20 p-3 text-center">
                <p className="text-xs text-neutral-400">Trims</p>
                <p className="text-xl font-bold text-yellow-300">{preview.summary.totalTrims}</p>
              </div>
              <div className="rounded-xl bg-neutral-700/50 p-3 text-center">
                <p className="text-xs text-neutral-400">Turnover</p>
                <p className="text-xl font-bold text-white">{formatPct(preview.summary.turnoverPct)}</p>
              </div>
              <div className="rounded-xl bg-neutral-700/50 p-3 text-center">
                <p className="text-xs text-neutral-400">Est. Cost</p>
                <p className="text-xl font-bold text-white">{preview.summary.estimatedCostBps} bps</p>
              </div>
            </div>

            {/* Actions List */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {preview.actions.map((action, idx) => (
                <div
                  key={`${action.symbol}-${idx}`}
                  className="flex items-center justify-between rounded-xl bg-neutral-900/50 p-3"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`badge text-xs px-2 py-1 border ${getActionColor(action.type)}`}
                    >
                      {action.type}
                    </span>
                    <span className="font-semibold text-white">{action.symbol}</span>
                    <span className="text-sm text-neutral-400">{action.sector}</span>
                  </div>
                  <div className="text-right">
                    <p className={`font-mono text-sm ${action.changeShares > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {action.changeShares > 0 ? '+' : ''}{action.changeShares} shares
                    </p>
                    <p className="text-xs text-neutral-500">{action.reason}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Risk Checks */}
      <div className="rounded-2xl border border-white/10 bg-neutral-800/60 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Risk Checks</h3>
          <span
            className={`badge text-sm px-3 py-1 ${
              preview.riskChecks.allPassed
                ? 'bg-emerald-500/20 text-emerald-100 border border-emerald-500/30'
                : 'bg-red-500/20 text-red-100 border border-red-500/30'
            }`}
          >
            {preview.riskChecks.allPassed ? 'All Passed' : 'Issues Found'}
          </span>
        </div>

        <div className="space-y-3">
          {preview.riskChecks.checks.map((check, idx) => (
            <div
              key={idx}
              className={`flex items-center justify-between rounded-xl p-3 ${
                check.passed ? 'bg-emerald-500/5' : 'bg-red-500/5'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    check.passed ? 'bg-emerald-500/20' : 'bg-red-500/20'
                  }`}
                >
                  {check.passed ? (
                    <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>
                <span className={check.passed ? 'text-neutral-300' : 'text-red-200'}>
                  {check.name}
                </span>
              </div>
              <span className="text-sm text-neutral-400">{check.message}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Portfolio Comparison */}
      {preview.currentPortfolio && preview.projectedPortfolio && (
        <div className="rounded-2xl border border-white/10 bg-neutral-800/60 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Portfolio Impact</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Current Portfolio */}
            <div className="rounded-xl bg-neutral-900/50 p-4">
              <h4 className="text-sm font-medium text-neutral-400 mb-3">Current</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-400">Value</span>
                  <span className="text-white font-medium">
                    {formatCurrency(preview.currentPortfolio.totalValue)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-400">Cash Buffer</span>
                  <span className="text-white font-medium">
                    {formatPct(preview.currentPortfolio.cashBufferPct)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-400">Gross Exposure</span>
                  <span className="text-white font-medium">
                    {formatPct(preview.currentPortfolio.grossExposurePct)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-400">Positions</span>
                  <span className="text-white font-medium">
                    {preview.currentPortfolio.positionCount}
                  </span>
                </div>
              </div>
            </div>

            {/* Projected Portfolio */}
            <div className="rounded-xl bg-neutral-900/50 p-4">
              <h4 className="text-sm font-medium text-neutral-400 mb-3">After Rotation</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-400">Value</span>
                  <span className="text-white font-medium">
                    {formatCurrency(preview.projectedPortfolio.totalValue)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-400">Cash Buffer</span>
                  <span className="text-white font-medium">
                    {formatPct(preview.projectedPortfolio.cashBufferPct)}
                    {preview.projectedPortfolio.cashBufferPct !== preview.currentPortfolio.cashBufferPct && (
                      <span className={`ml-2 text-xs ${
                        preview.projectedPortfolio.cashBufferPct > preview.currentPortfolio.cashBufferPct
                          ? 'text-emerald-400'
                          : 'text-red-400'
                      }`}>
                        ({preview.projectedPortfolio.cashBufferPct > preview.currentPortfolio.cashBufferPct ? '+' : ''}
                        {formatPct(preview.projectedPortfolio.cashBufferPct - preview.currentPortfolio.cashBufferPct)})
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-400">Gross Exposure</span>
                  <span className="text-white font-medium">
                    {formatPct(preview.projectedPortfolio.grossExposurePct)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-400">Positions</span>
                  <span className="text-white font-medium">
                    {preview.projectedPortfolio.positionCount}
                    {preview.projectedPortfolio.positionCount !== preview.currentPortfolio.positionCount && (
                      <span className={`ml-2 text-xs ${
                        preview.projectedPortfolio.positionCount > preview.currentPortfolio.positionCount
                          ? 'text-emerald-400'
                          : 'text-red-400'
                      }`}>
                        ({preview.projectedPortfolio.positionCount > preview.currentPortfolio.positionCount ? '+' : ''}
                        {preview.projectedPortfolio.positionCount - preview.currentPortfolio.positionCount})
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Skip Rotation Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowSkipModal(true)}
          className="btn btn-outline text-red-400 border-red-500/30 hover:bg-red-500/10 px-4 py-2"
          type="button"
        >
          Skip This Rotation
        </button>
      </div>

      {/* Skip Modal */}
      {showSkipModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-neutral-900 p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Skip Rotation</h3>
            <p className="text-neutral-400 mb-4">
              Are you sure you want to skip the next scheduled rotation? This action will be logged.
            </p>
            <label className="block mb-4">
              <span className="text-sm text-neutral-400">Reason (required)</span>
              <input
                type="text"
                value={skipReason}
                onChange={(e) => setSkipReason(e.target.value)}
                className="mt-1 w-full rounded-lg bg-neutral-800 border border-white/10 px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
                placeholder="Enter reason for skipping..."
              />
            </label>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowSkipModal(false);
                  setSkipReason('');
                }}
                className="btn btn-outline px-4 py-2"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleSkip}
                disabled={!skipReason.trim()}
                className="btn px-4 py-2 bg-red-500 text-white hover:brightness-110 disabled:opacity-50"
                type="button"
              >
                Skip Rotation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NextRunTab;
